import {
  AHIcon,
  AHSeparator,
  ListItem,
  SectionHeader,
  useLayoutContext,
} from "arkh";
import { FlatList } from "react-native";
import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { useSetCurrentStoreId } from "../../OS/Login/State";

import { FormattedMessage } from "react-intl";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery } from "react-relay";
import { UserSetStoreScreenQuery } from "./__generated__/UserSetStoreScreenQuery.graphql";
import { ProfileStackScreenProps } from "./ProfileStack";
import { Features, hasFeatureInTier } from "../../useHasFeatureInTier";
import { useIsStoresprintAdmin } from "@/src/useHasFeatureFlag";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

type Props = ProfileStackScreenProps<"SetStore">;

export function UserSetStoreScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerUserSetStoreScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerUserSetStoreScreen(props: Props & { fetchKey: string }) {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<UserSetStoreScreenQuery>(
    graphql`
      query UserSetStoreScreenQuery {
        store {
          id
        }
        me @required(action: THROW) {
          id
          stores {
            id
            name
            tier
            timezone
          }
          currentStoreId
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const dirty = data?.me?.currentStoreId !== props.route.params.storeId;

  const setCurrentStoreId = useSetCurrentStoreId();

  const onSetStore = useCallback(() => {
    const selectedStore = data.me.stores.find(
      ({ id }) => id === props.route.params.storeId
    );
    if (!selectedStore) {
      return;
    }
    setCurrentStoreId(props.route.params.storeId, selectedStore.timezone);

    if (!hasFeatureInTier(Features.OPERATIONS_PLATFORM, selectedStore.tier)) {
      props.navigation.navigateDeprecated("Dashboard", {});
    } else {
      props.navigation.replace("Team", {});
    }
  }, [
    data.me.stores,
    props.navigation,
    props.route.params.storeId,
    setCurrentStoreId,
  ]);

  const getHeaderOptions = useNavigationHeaderOptions();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getHeaderOptions({
        type: "SAVE",
        onSave: onSetStore,
        onCancel: () => {
          props.navigation.goBack();
        },
        title: intl_tokens.SelectStore,
        saveLoading: false,
      })
    );
  }, [
    props.navigation,
    dirty,
    onSetStore,
    getHeaderOptions,
    intl_tokens.SelectStore,
  ]);

  const canCreateStore = useIsStoresprintAdmin();
  const listData = useMemo(() => {
    return [
      ...(data?.me.stores ?? [])
        .slice()
        .sort((a, b) =>
          a.name
            .toLowerCase()
            .trimStart()
            .localeCompare(b.name.toLowerCase().trimStart())
        ),
      ...(canCreateStore ? ["CREATE_STORE" as const] : []),
    ];
  }, [data?.me.stores, canCreateStore]);
  return (
    <FlatList
      contentContainerStyle={{
        paddingTop: layout.space.medium,
        paddingHorizontal: layout.space.medium,
        paddingBottom: insets.bottom + layout.space.medium,
      }}
      keyExtractor={(item) => (item === "CREATE_STORE" ? item : item.id)}
      data={listData}
      ItemSeparatorComponent={AHSeparator}
      ListHeaderComponent={
        <>
          <SectionHeader>
            <SectionHeader.Title>
              <FormattedMessage
                defaultMessage="Stores"
                description="MyProfile:SetStoreScreen:listHeader"
              />
            </SectionHeader.Title>
          </SectionHeader>
        </>
      }
      renderItem={({ item, index }) => {
        if (item === "CREATE_STORE") {
          return (
            <ListItem
              isLast
              left={<AHIcon type="add" size="small" color="primary" />}
              onPress={() => {
                props.navigation.navigateDeprecated("CreateStore", {});
              }}
            >
              <ListItem.Title>
                <FormattedMessage
                  defaultMessage="Create Store"
                  description="MyProfile:SetStoreScreen:CreateBtn"
                />
              </ListItem.Title>
            </ListItem>
          );
        }
        return (
          <ListItem
            isFirst={index === 0}
            isLast={!canCreateStore && index === listData.length - 1}
            onPress={() => {
              props.navigation.setParams({
                storeId: item.id,
              });
            }}
            right={
              props.route.params.storeId === item.id ? <ListItem.Check /> : null
            }
          >
            <ListItem.Title>
              {item.name || (
                <FormattedMessage
                  defaultMessage="Untitled Store"
                  description="MyProfile: store without name, default display name"
                />
              )}
            </ListItem.Title>
          </ListItem>
        );
      }}
    />
  );
}
